import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaList,
  FaListOl,
  FaListUl,
  FaCircle,
  FaSquare,
  FaSortNumericDown,
  FaSortAlphaDown,
  FaExclamationTriangle,
  FaIndent
} from 'react-icons/fa';

const HTMLLesson17 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Basic unordered list -->
<ul>
  <li>Web Development</li>
  <li>Mobile Development</li>
  <li>Data Science</li>
  <li>UI/UX Design</li>
</ul>

<!-- Unordered list with nested items -->
<ul>
  <li>Frontend Technologies
    <ul>
      <li>HTML</li>
      <li>CSS</li>
      <li>JavaScript</li>
    </ul>
  </li>
  <li>Backend Technologies
    <ul>
      <li>Node.js</li>
      <li>Python</li>
      <li>Java</li>
    </ul>
  </li>
</ul>`;

  const code2 = `<!-- Basic ordered list -->
<ol>
  <li>Preheat oven to 350°F</li>
  <li>Mix dry ingredients</li>
  <li>Add wet ingredients</li>
  <li>Pour into baking pan</li>
  <li>Bake for 30 minutes</li>
</ol>

<!-- Ordered list with custom start -->
<ol start="10">
  <li>Item number 10</li>
  <li>Item number 11</li>
  <li>Item number 12</li>
</ol>

<!-- Reversed ordered list -->
<ol reversed>
  <li>Third item</li>
  <li>Second item</li>
  <li>First item</li>
</ol>`;

  const code3 = `<!-- Disc (default) -->
<ul style="list-style-type: disc">
  <li>Default bullet points</li>
  <li>Most common style</li>
  <li>Solid circles</li>
</ul>

<!-- Circle -->
<ul style="list-style-type: circle">
  <li>Hollow circles</li>
  <li>Lighter appearance</li>
  <li>Good for nested lists</li>
</ul>

<!-- Square -->
<ul style="list-style-type: square">
  <li>Square bullets</li>
  <li>More modern look</li>
  <li>Good for technical content</li>
</ul>

<!-- None -->
<ul style="list-style-type: none">
  <li>No bullets</li>
  <li>Clean, minimal design</li>
  <li>Perfect for navigation</li>
</ul>`;

  const code4 = `<!-- Numbers (default) -->
<ol type="1">
  <li>Step one</li>
  <li>Step two</li>
  <li>Step three</li>
</ol>

<!-- Uppercase Letters -->
<ol type="A">
  <li>Section A</li>
  <li>Section B</li>
  <li>Section C</li>
</ol>

<!-- Lowercase Letters -->
<ol type="a">
  <li>Option a</li>
  <li>Option b</li>
  <li>Option c</li>
</ol>

<!-- Uppercase Roman -->
<ol type="I">
  <li>Chapter I</li>
  <li>Chapter II</li>
  <li>Chapter III</li>
</ol>

<!-- Lowercase Roman -->
<ol type="i">
  <li>Part i</li>
  <li>Part ii</li>
  <li>Part iii</li>
</ol>`;

  const code5 = `<!-- Description List -->
<dl>
  <dt>HTML</dt>
  <dd>HyperText Markup Language - the standard markup language for web pages</dd>
  
  <dt>CSS</dt>
  <dd>Cascading Style Sheets - used for styling web pages</dd>
  <dd>Controls layout, colors, fonts, and responsive design</dd>
  
  <dt>JavaScript</dt>
  <dd>Programming language for interactive web content</dd>
  <dd>Enables dynamic behavior and user interactions</dd>
</dl>

<!-- Complex description list -->
<dl>
  <dt>Frontend Development</dt>
  <dd>Client-side programming</dd>
  <dd>User interface and experience</dd>
  
  <dt>Backend Development</dt>
  <dd>Server-side programming</dd>
  <dd>Database management and APIs</dd>
  
  <dt>Full Stack Development</dt>
  <dd>Combination of frontend and backend skills</dd>
</dl>`;

  const code6 = `<!-- Nested lists example -->
<ul>
  <li>Web Development
    <ul>
      <li>Frontend
        <ol>
          <li>HTML</li>
          <li>CSS</li>
          <li>JavaScript</li>
        </ol>
      </li>
      <li>Backend
        <ol>
          <li>Node.js</li>
          <li>Python</li>
          <li>Databases</li>
        </ol>
      </li>
    </ul>
  </li>
  <li>Mobile Development
    <ul>
      <li>iOS Development
        <ul>
          <li>Swift</li>
          <li>Objective-C</li>
        </ul>
      </li>
      <li>Android Development
        <ul>
          <li>Java</li>
          <li>Kotlin</li>
        </ul>
      </li>
    </ul>
  </li>
</ul>`;

  const code7 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* Modern list styling */
    .list-system {
      max-width: 800px;
      margin: 2rem auto;
      padding: 2rem;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    
    .list-demo {
      margin: 2rem 0;
      padding: 1.5rem;
      background: #f8fafc;
      border-radius: 8px;
      border-left: 4px solid #3b82f6;
    }
    
    /* Custom list styles */
    .custom-disc {
      list-style-type: disc;
      color: #3b82f6;
    }
    
    .custom-circle {
      list-style-type: circle;
      color: #059669;
    }
    
    .custom-square {
      list-style-type: square;
      color: #dc2626;
    }
    
    .custom-none {
      list-style-type: none;
      padding-left: 0;
    }
    
    .custom-none li {
      padding: 0.5rem 0;
      border-bottom: 1px solid #e2e8f0;
    }
    
    .custom-none li:before {
      content: "▸";
      color: #7c3aed;
      margin-right: 0.5rem;
    }
    
    /* Numbered list styles */
    .numbered-decimal {
      list-style-type: decimal;
    }
    
    .numbered-upper-alpha {
      list-style-type: upper-alpha;
      color: #d97706;
    }
    
    .numbered-lower-roman {
      list-style-type: lower-roman;
      color: #0891b2;
    }
    
    /* Description list styling */
    .styled-dl dt {
      font-weight: bold;
      color: #1f2937;
      margin-top: 1rem;
      font-size: 1.1rem;
    }
    
    .styled-dl dd {
      margin-left: 1rem;
      color: #6b7280;
      margin-bottom: 0.5rem;
    }
    
    /* Navigation list */
    .nav-list {
      list-style-type: none;
      padding: 0;
      display: flex;
      gap: 2rem;
      background: #1f2937;
      padding: 1rem 2rem;
      border-radius: 8px;
    }
    
    .nav-list li a {
      color: white;
      text-decoration: none;
      font-weight: 500;
      transition: color 0.3s ease;
    }
    
    .nav-list li a:hover {
      color: #60a5fa;
    }
  </style>
</head>
<body>
  <div class="list-system">
    <h1>HTML Lists Implementation Guide</h1>
    
    <!-- Navigation Menu using List -->
    <nav>
      <ul class="nav-list">
        <li><a href="#unordered">Unordered Lists</a></li>
        <li><a href="#ordered">Ordered Lists</a></li>
        <li><a href="#description">Description Lists</a></li>
        <li><a href="#nested">Nested Lists</a></li>
      </ul>
    </nav>
    
    <!-- Unordered List Examples -->
    <section class="list-demo">
      <h2 id="unordered">Unordered List Styles</h2>
      
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2rem;">
        <div>
          <h3>Disc (Default)</h3>
          <ul class="custom-disc">
            <li>Home Page</li>
            <li>About Us</li>
            <li>Services</li>
            <li>Contact</li>
          </ul>
        </div>
        
        <div>
          <h3>Circle</h3>
          <ul class="custom-circle">
            <li>Feature 1</li>
            <li>Feature 2</li>
            <li>Feature 3</li>
          </ul>
        </div>
        
        <div>
          <h3>Square</h3>
          <ul class="custom-square">
            <li>Step 1</li>
            <li>Step 2</li>
            <li>Step 3</li>
          </ul>
        </div>
        
        <div>
          <h3>Custom None</h3>
          <ul class="custom-none">
            <li>Dashboard</li>
            <li>Profile</li>
            <li>Settings</li>
            <li>Logout</li>
          </ul>
        </div>
      </div>
    </section>
    
    <!-- Ordered List Examples -->
    <section class="list-demo">
      <h2 id="ordered">Ordered List Types</h2>
      
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 2rem;">
        <div>
          <h3>Numbers (1, 2, 3)</h3>
          <ol class="numbered-decimal">
            <li>Requirements</li>
            <li>Design</li>
            <li>Development</li>
          </ol>
        </div>
        
        <div>
          <h3>Uppercase (A, B, C)</h3>
          <ol class="numbered-upper-alpha">
            <li>Introduction</li>
            <li>Methods</li>
            <li>Results</li>
          </ol>
        </div>
        
        <div>
          <h3>Roman (i, ii, iii)</h3>
          <ol class="numbered-lower-roman">
            <li>Preface</li>
            <li>Chapter 1</li>
            <li>Chapter 2</li>
          </ol>
        </div>
      </div>
    </section>
    
    <!-- Description List Example -->
    <section class="list-demo">
      <h2 id="description">Description Lists</h2>
      <dl class="styled-dl">
        <dt>Semantic HTML</dt>
        <dd>Using HTML elements according to their meaning</dd>
        <dd>Improves accessibility and SEO</dd>
        
        <dt>Responsive Design</dt>
        <dd>Websites that work on all devices</dd>
        <dd>Uses flexible layouts and media queries</dd>
        
        <dt>Progressive Enhancement</dt>
        <dd>Building from basic to advanced features</dd>
        <dd>Ensures core functionality for all users</dd>
      </dl>
    </section>
    
    <!-- Nested List Example -->
    <section class="list-demo">
      <h2 id="nested">Complex Nested Lists</h2>
      <ul>
        <li>Development Team
          <ul>
            <li>Frontend Developers
              <ol>
                <li>UI Specialist</li>
                <li>UX Designer</li>
                <li>JavaScript Developer</li>
              </ol>
            </li>
            <li>Backend Developers
              <ol>
                <li>API Developer</li>
                <li>Database Administrator</li>
                <li>DevOps Engineer</li>
              </ol>
            </li>
          </ul>
        </li>
        <li>Marketing Team
          <ul>
            <li>Content Writers</li>
            <li>SEO Specialists</li>
            <li>Social Media Managers</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Lists Introduction",
      content: "Lists organize content in structured formats, making information easier to scan and understand for users",
      keyPoints: [
        "Three main types: unordered, ordered, and description lists",
        "Unordered lists use bullet points",
        "Ordered lists use numbers or letters",
        "Description lists define terms and descriptions"
      ],
      icon: <FaList className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Unordered Lists Deep Dive", 
      content: "Use unordered lists for items without specific sequence or hierarchy, perfect for features, options, and navigation",
      keyPoints: [
        "Created with <ul> element",
        "List items with <li> tags",
        "Default bullet style: disc",
        "Supports nested lists for hierarchies"
      ],
      icon: <FaListUl className="section-icon" />,
      color: "#059669",
      code: code1
    },
    {
      title: "Ordered Lists and Sequencing",
      content: "Ordered lists display items in specific sequence, ideal for steps, rankings, and procedural content",
      keyPoints: [
        "Created with <ol> element",
        "Automatic numbering system",
        "Custom start numbers with start attribute",
        "Reversed order with reversed attribute"
      ],
      icon: <FaListOl className="section-icon" />,
      color: "#dc2626",
      code: code2
    },
    {
      title: "List Style Customization",
      content: "Customize list appearance with different marker styles for both unordered and ordered lists",
      keyPoints: [
        "Unordered: disc, circle, square, none",
        "Ordered: numbers, letters, roman numerals",
        "CSS list-style-type property",
        "Custom markers with CSS pseudo-elements"
      ],
      icon: <FaCircle className="section-icon" />,
      color: "#7c3aed",
      code: code3
    },
    {
      title: "Description Lists",
      content: "Use description lists for term-definition pairs, perfect for glossaries, metadata, and key-value information",
      keyPoints: [
        "<dl> for definition list container",
        "<dt> for definition term",
        "<dd> for definition description",
        "Multiple descriptions per term supported"
      ],
      icon: <FaIndent className="section-icon" />,
      color: "#d97706",
      code: code5
    },
    {
      title: "Advanced List Techniques",
      content: "Create complex information hierarchies with nested lists and combine different list types for rich content",
      keyPoints: [
        "Nest lists within list items",
        "Mix ordered and unordered lists",
        "Maintain proper indentation",
        "Ensure accessibility with proper structure"
      ],
      icon: <FaSortNumericDown className="section-icon" />,
      color: "#0891b2",
      code: code6
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 17 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Lists Mastery</h1>
              <p className="html-lesson-subtitle">
                Master content organization with comprehensive list structures, styling, and advanced techniques
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual List Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                List Structure Power
              </div>
              <h3 className="html-visual-title">
                Organize Content with Multiple List Types and Styles
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>List Syntax Foundation</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<!-- Unordered List -->
<ul>
  <li>Item 1</li>
  <li>Item 2</li>
</ul>

<!-- Ordered List -->
<ol>
  <li>First item</li>
  <li>Second item</li>
</ol>

<!-- Description List -->
<dl>
  <dt>Term</dt>
  <dd>Description</dd>
</dl>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Live List Examples</h4>
                <div className="html-list-preview">
                  <div className="list-demo-item">
                    <ul className="preview-list">
                      <li>Unordered List</li>
                      <li>Bullet points</li>
                    </ul>
                  </div>
                  <div className="list-demo-item">
                    <ol className="preview-list">
                      <li>Ordered List</li>
                      <li>Numbered items</li>
                    </ol>
                  </div>
                  <div className="list-demo-item">
                    <dl className="preview-list">
                      <dt>Description List</dt>
                      <dd>Term definitions</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Remember:</strong> Choose the right list type based on your content structure and semantic meaning!</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* List Types Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaList className="html-badge-icon" />
                HTML List Types Guide
              </div>
              <h3 className="html-comparison-title">
                When to Use Each List Type
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>List Type</th>
                    <th>Element</th>
                    <th>Default Style</th>
                    <th>Best For</th>
                    <th>Example Use Cases</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Unordered List</strong></td>
                    <td><code>&lt;ul&gt;</code></td>
                    <td>Bullet points (disc)</td>
                    <td>Items without sequence</td>
                    <td>Features, options, navigation menus</td>
                  </tr>
                  <tr>
                    <td><strong>Ordered List</strong></td>
                    <td><code>&lt;ol&gt;</code></td>
                    <td>Numbers (1, 2, 3)</td>
                    <td>Sequential items</td>
                    <td>Instructions, rankings, steps</td>
                  </tr>
                  <tr>
                    <td><strong>Description List</strong></td>
                    <td><code>&lt;dl&gt;</code></td>
                    <td>Term-description pairs</td>
                    <td>Definitions, metadata</td>
                    <td>Glossaries, FAQs, key-value data</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* List Best Practices */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                List Best Practices
              </div>
              <h3 className="html-practices-title">
                Professional List Implementation Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do These</h4>
                <ul>
                  <li>Use semantic list types appropriately</li>
                  <li>Maintain consistent indentation for nested lists</li>
                  <li>Use description lists for term-definition pairs</li>
                  <li>Style lists with CSS for better presentation</li>
                  <li>Ensure proper accessibility with screen readers</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li>Using lists for layout (use CSS Grid/Flexbox)</li>
                  <li>Excessively deep nesting (keep it to 3-4 levels max)</li>
                  <li>Mixing list types without clear purpose</li>
                  <li>Forgetting to close list item tags</li>
                  <li>Using paragraphs inside list items unnecessarily</li>
                </ul>
              </div>
            </div>
          </div>

          {/* List Style Options */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaSortAlphaDown className="html-badge-icon" />
                List Style Options
              </div>
              <h3 className="html-multiple-title">
                Complete Guide to List Markers and Numbering
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>Unordered List Markers:</h4>
                <ul>
                  <li><strong>disc</strong> - Default solid bullet points</li>
                  <li><strong>circle</strong> - Hollow circular bullets</li>
                  <li><strong>square</strong> - Square-shaped bullets</li>
                  <li><strong>none</strong> - No visible markers</li>
                </ul>
                
                <h4>Ordered List Types:</h4>
                <ul>
                  <li><strong>1</strong> - Decimal numbers (default)</li>
                  <li><strong>A</strong> - Uppercase letters</li>
                  <li><strong>a</strong> - Lowercase letters</li>
                  <li><strong>I</strong> - Uppercase Roman numerals</li>
                  <li><strong>i</strong> - Lowercase Roman numerals</li>
                </ul>
              </div>
              
              <div className="html-style-examples">
                <h4>Quick Reference:</h4>
                <div className="style-grid">
                  <div className="style-item">
                    <div className="style-icon">•</div>
                    <span>disc</span>
                  </div>
                  <div className="style-item">
                    <div className="style-icon">◦</div>
                    <span>circle</span>
                  </div>
                  <div className="style-item">
                    <div className="style-icon">■</div>
                    <span>square</span>
                  </div>
                  <div className="style-item">
                    <div className="style-icon">1</div>
                    <span>decimal</span>
                  </div>
                  <div className="style-item">
                    <div className="style-icon">A</div>
                    <span>upper-alpha</span>
                  </div>
                  <div className="style-item">
                    <div className="style-icon">i</div>
                    <span>lower-roman</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional List System Implementation
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code7}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete List Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML lists?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive list implementation including unordered, ordered, and description lists, advanced nesting techniques, and professional styling. Start creating well-organized, accessible content!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-text-format" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson17;